import React from 'react';
import { Card, Skeleton, SkeletonButton, SkeletonInput, SkeletonText } from '@parsina/framework';
import { C, CT } from '../../constants/theme';
import Panel from '../../components/common/Panel.jsx';
import {
  SkeletonPage, InnerHeaderSkeleton, MobileHeaderSkeleton, SectionNavSkeleton,
  SegmentedSkeleton, OdontogramSkeleton,
} from '../../components/common/skeletons.jsx';

// جلسهٔ درمان — the loading shapes of the visit workspace.
//
// The workspace is EIGHT independent fetches on one screen (the visit itself,
// the chart, the note, prior visits, the plan, medications, orders, diagnoses,
// files), and DESIGN.md §"Loading & skeleton" says each region skeletons ALONE:
// the odontogram panel shimmers while شرح حال is already typed into. So this
// file exports one skeleton per region — the three rail panels and the five
// panes — and the page skeleton (drawn only until the visit ITSELF resolves,
// before any region can start) is composed from the same pieces, so the shape
// the page loads into is the shape its regions load into.
//
// Every region skeleton keeps the REAL Panel chrome with its real title: the
// title is known before the data is, and «چارت دندان» over a shimmering arch
// says what is coming in a way a bare rectangle cannot.

// ── shared bits ─────────────────────────────────────────────────────────────

/** The pane's add-form box: bordered `surface2` panel, `fields` inputs, a button row. */
const FormBoxSkeleton = ({ fields = 3, columns = 'repeat(auto-fit, minmax(150px, 1fr))', chips = 0 }) => (
  <div
    aria-hidden
    style={{
      border: `1px solid ${C.border}`, borderRadius: 10, padding: 12,
      marginBottom: 14, background: CT.surface2,
    }}
  >
    {chips > 0 && (
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {Array.from({ length: chips }, (_, i) => <Skeleton key={i} width={[104, 96, 88][i % 3]} height={36} radius={999} />)}
      </div>
    )}
    <div style={{ display: 'grid', gridTemplateColumns: columns, gap: 10 }}>
      {Array.from({ length: fields }, (_, i) => <SkeletonInput key={i} labelWidth={64} />)}
    </div>
    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
      <SkeletonButton width={104} />
      <SkeletonButton width={96} />
    </div>
  </div>
);

/** A pane's list row: bordered card, 13px title + 11px meta, status pill. */
const ListRowSkeleton = ({ i, lead = false, actions = 0 }) => (
  <div
    aria-hidden
    style={{
      border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px',
      background: CT.surface,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {lead && <Skeleton width={38} height={22} radius={999} />}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Skeleton width={`${38 + ((i * 13) % 30)}%`} height={13} />
        <Skeleton width={`${24 + ((i * 7) % 20)}%`} height={11} />
      </div>
      <Skeleton width={64} height={22} radius={8} />
    </div>
    {actions > 0 && (
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        {Array.from({ length: actions }, (_, k) => <SkeletonButton key={k} width={84} />)}
      </div>
    )}
  </div>
);

const ListSkeleton = ({ count = 3, lead, actions }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {Array.from({ length: count }, (_, i) => <ListRowSkeleton key={i} i={i} lead={lead} actions={actions} />)}
  </div>
);

// ── the rail: three persistent panels ───────────────────────────────────────

/** VisitOdontogram's Stat: 74px min, 10.5px label over a 16px number. */
const RailStatSkeleton = () => (
  <div
    style={{
      background: CT.surface, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '8px 12px', minWidth: 74, display: 'flex', flexDirection: 'column', gap: 5,
    }}
  >
    <Skeleton width={40} height={10} />
    <Skeleton width={22} height={16} />
  </div>
);

export const OdontogramPanelSkeleton = ({ t, label }) => (
  <Panel
    title={t('visit.chart.title')}
    action={(
      <div aria-hidden style={{ display: 'flex', gap: 6 }}>
        {[0, 1, 2].map((i) => <RailStatSkeleton key={i} />)}
      </div>
    )}
  >
    <SkeletonPage label={label} data-testid="visit-chart-skeleton">
      <OdontogramSkeleton />
      <div style={{ marginTop: 10, padding: '10px 12px', background: CT.surface2, borderRadius: 10, minHeight: 44 }}>
        <Skeleton width="60%" height={12} style={{ marginTop: 6 }} />
      </div>
    </SkeletonPage>
  </Panel>
);

export const SubjectivePanelSkeleton = ({ t, label, collapsible, defaultOpen }) => (
  <Panel
    title={t('visit.subjective.title')}
    collapsible={collapsible}
    defaultOpen={defaultOpen}
    action={<SkeletonButton width={88} />}
  >
    <SkeletonPage label={label} data-testid="visit-subjective-skeleton">
      {/* «شرح حال بیمار» — the integrated history strip. */}
      <div style={{ background: CT.surface2, borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
        <Skeleton width={96} height={11} style={{ marginBottom: 8 }} />
        <SkeletonText lines={3} height={12} lastWidth="55%" />
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        <SkeletonInput labelWidth={96} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton width={72} height={12} />
          <div style={{ display: 'flex', gap: 6 }}>
            <Skeleton width={48} height={36} radius={999} />
            <Skeleton width={48} height={36} radius={999} />
            <SkeletonButton width={132} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <SkeletonInput labelWidth={64} />
          <SkeletonInput labelWidth={64} />
        </div>
        <SkeletonInput labelWidth={80} multiline />
      </div>
    </SkeletonPage>
  </Panel>
);

export const PlanPanelSkeleton = ({ t, label, collapsible, defaultOpen }) => (
  <Panel
    title={t('visit.plan.title')}
    collapsible={collapsible}
    defaultOpen={defaultOpen}
    action={(
      <div aria-hidden style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Skeleton width={64} height={22} radius={999} />
        <SkeletonButton width={96} />
      </div>
    )}
  >
    <SkeletonPage label={label} data-testid="visit-plan-skeleton">
      <ListSkeleton count={3} lead />
    </SkeletonPage>
  </Panel>
);

/** The lazy «سابقهٔ دندان» list under a selected tooth: dated one-line rows. */
export const ToothHistorySkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="visit-tooth-history-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Skeleton width={64} height={11} />
        <Skeleton width={`${40 + ((i * 17) % 30)}%`} height={12} />
      </div>
    ))}
  </SkeletonPage>
);

/** «مراجعات قبلی»: the 44px outline-button rows each prior visit becomes. */
export const PriorVisitsSkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="visit-prior-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {[0, 1].map((i) => <Skeleton key={i} width="100%" height={44} radius={10} />)}
  </SkeletonPage>
);

// ── the workspace panes ─────────────────────────────────────────────────────

export const DiagnosisPaneSkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="visit-diagnosis-skeleton">
    <FormBoxSkeleton fields={3} columns="minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)" />
    <ListSkeleton count={3} />
  </SkeletonPage>
);

export const OrdersPaneSkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="visit-orders-skeleton">
    <FormBoxSkeleton fields={3} chips={2} columns="repeat(auto-fit, minmax(160px, 1fr))" />
    <ListSkeleton count={3} actions={1} />
  </SkeletonPage>
);

export const FilesPaneSkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="visit-files-skeleton">
    <FormBoxSkeleton fields={2} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[0, 1].map((i) => (
        <div
          key={i}
          aria-hidden
          style={{
            border: `1px solid ${C.border}`, borderRadius: 10, padding: 12,
            display: 'flex', gap: 12, alignItems: 'center', background: CT.surface,
          }}
        >
          <Skeleton width={72} height={72} radius={8} />
          <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton width="45%" height={13} />
            <Skeleton width="30%" height={11} />
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <SkeletonButton size="sm" width={72} />
              <SkeletonButton size="sm" width={72} />
            </div>
          </div>
        </div>
      ))}
    </div>
  </SkeletonPage>
);

/** MedicationsPane fetches nothing on entry; this is its shape for the PAGE skeleton. */
export const MedicationsPaneSkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="visit-meds-skeleton">
    <div aria-hidden style={{ marginBottom: 14 }}>
      <Skeleton width={96} height={12} style={{ marginBottom: 8 }} />
      <SkeletonInput label={false} />
    </div>
    <ListSkeleton count={2} actions={1} />
  </SkeletonPage>
);

/** QuestionsPane's generate box (button + cost note) over the Q&A section. */
export const QuestionsPaneSkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="visit-questions-skeleton">
    <div aria-hidden style={{ marginBottom: 14 }}>
      <SkeletonButton width={160} />
      <Skeleton width={140} height={11} style={{ marginTop: 6 }} />
    </div>
    <div aria-hidden style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
      <Skeleton width={110} height={11} style={{ marginBottom: 8 }} />
      <SkeletonInput label={false} />
    </div>
  </SkeletonPage>
);

/**
 * The region the generated question sets land in: a diagnosis title with a
 * count pill, then question cards. Drawn while «تولید پرسش‌ها» is running — the
 * button already says it is generating, so what is missing is the SHAPE of
 * what it will produce.
 */
export const QuestionSetSkeleton = ({ label }) => (
  <SkeletonPage label={label} data-testid="visit-questionset-skeleton" style={{ marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
      <Skeleton width={120} height={12} />
      <Skeleton width={56} height={22} radius={999} />
    </div>
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        style={{
          border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px',
          marginBottom: 8, background: CT.surface,
        }}
      >
        <Skeleton width={`${55 + ((i * 17) % 35)}%`} height={13} style={{ marginBottom: 8 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <Skeleton width={64} height={36} radius={999} />
          <Skeleton width={64} height={36} radius={999} />
          <Skeleton width={64} height={36} radius={999} />
        </div>
      </div>
    ))}
  </SkeletonPage>
);

const PANE = {
  diagnosis: DiagnosisPaneSkeleton,
  meds: MedicationsPaneSkeleton,
  orders: OrdersPaneSkeleton,
  questions: QuestionsPaneSkeleton,
  files: FilesPaneSkeleton,
};

// ── the pages ───────────────────────────────────────────────────────────────

export const VisitSkeletonWeb = ({ t, tab = 'diagnosis', label }) => {
  const Pane = PANE[tab] || DiagnosisPaneSkeleton;
  return (
    <SkeletonPage
      label={label}
      data-testid="visit-content"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}
    >
      <InnerHeaderSkeleton avatar meta actions={2} />
      <div
        style={{
          flex: 1, minHeight: 0, display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
          gap: 16, padding: '16px 24px 24px', overflow: 'hidden',
        }}
      >
        <div className="visit-rail" style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 14, paddingInlineEnd: 4 }}>
          <OdontogramPanelSkeleton t={t} />
          <SubjectivePanelSkeleton t={t} />
          <PlanPanelSkeleton t={t} />
        </div>
        <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flexShrink: 0, marginBottom: 12 }}>
            <SegmentedSkeleton count={5} />
          </div>
          <Card style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: 16 }}>
            <Pane />
          </Card>
        </div>
      </div>
    </SkeletonPage>
  );
};

export const VisitSkeletonMobile = ({ t, tab = 'diagnosis', label }) => {
  const Pane = PANE[tab] || DiagnosisPaneSkeleton;
  return (
    <SkeletonPage
      label={label}
      data-testid="visit-content"
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <MobileHeaderSkeleton back subtitle />
      <div style={{ padding: '0.75rem 0.75rem 0' }}>
        <OdontogramPanelSkeleton t={t} />
        <div style={{ height: '0.75rem' }} />
        <SubjectivePanelSkeleton t={t} collapsible defaultOpen={false} />
        <div style={{ height: '0.5rem' }} />
        <PlanPanelSkeleton t={t} collapsible defaultOpen={false} />
      </div>
      <div style={{ marginTop: '0.75rem' }}>
        <SectionNavSkeleton count={5} />
      </div>
      <div style={{ padding: '0.875rem 0.75rem 1.5rem' }}>
        <Card style={{ padding: '0.875rem', background: CT.surface }}>
          <Pane />
        </Card>
      </div>
    </SkeletonPage>
  );
};
